import React, { useState, useEffect, useMemo, useRef } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { calculateFifoAllocations, validateManualAllocations } from "../utils/paymentCalculations.js";
import { recordCustomerPaymentTally, updatePayment } from "../services/paymentService.js";
import { PaymentReceiptModal } from "./PaymentReceiptModal.jsx";
import { useInvoices } from "../hooks/useInvoices.js";
import { useCompanyParties } from "../../customers/hooks/useCompanyParties.js";
import { useActiveCompany } from "../../../shared/context/ActiveCompanyContext.jsx";

const PAYMENT_MODES = [
  "Cash",
  "UPI / GPay / PhonePe",
  "Bank Transfer / NEFT",
  "Cheque"
];

export function PaymentTallyModal({
  isOpen,
  onClose,
  preselectedCustomer = null,
  onPaymentSaved,
  editingPayment = null // existing PAYMENT_IN record to modify (party is locked)
}) {
  const isEditing = !!editingPayment?.id;
  // Working company (header): only its customers and invoices; receipts are saved under it
  const { invoices: allInvoices, refetch: refetchInvoices } = useInvoices();
  const { customers, refetch: refetchCustomers, dueOf } = useCompanyParties();
  const { activeCompany: defaultCompany, activeCompanyId, ownsRecord } = useActiveCompany();
  const invoices = useMemo(() => allInvoices.filter(ownsRecord), [allInvoices, ownsRecord]);

  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);

  const [amountReceived, setAmountReceived] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");

  const [allocations, setAllocations] = useState([]);
  const [isManualModified, setIsManualModified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [savedPaymentRecord, setSavedPaymentRecord] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // Old allocation per invoice for the payment being edited (treated as reversed while editing)
  const editAllocByInvoice = useMemo(() => {
    const map = {};
    (editingPayment?.allocations || []).forEach((a) => {
      if (a.invoiceId) map[a.invoiceId] = (map[a.invoiceId] || 0) + Number(a.allocatedAmount || 0);
    });
    return map;
  }, [editingPayment]);

  // Seed edit state once per opened payment (customers refetching must not wipe edits)
  const seededEditIdRef = useRef(null);
  useEffect(() => {
    if (!isOpen) {
      seededEditIdRef.current = null;
      return;
    }
    if (isEditing && seededEditIdRef.current !== editingPayment.id) {
      seededEditIdRef.current = editingPayment.id;
      setSelectedCustomerId(editingPayment.partyId || editingPayment.customerId);
      setAmountReceived(String(editingPayment.amount || ""));
      setPaymentDate(editingPayment.paymentDate || new Date().toISOString().split("T")[0]);
      setPaymentMode(editingPayment.paymentMode || "Cash");
      setReference(editingPayment.reference || "");
      setNotes(editingPayment.notes || "");
      setErrorMessage("");
      setIsManualModified(true); // keep the original split until the user changes the amount
    }
  }, [isOpen, isEditing, editingPayment]);

  // Initialize or sync selected customer
  useEffect(() => {
    if (isOpen && !isEditing) {
      if (preselectedCustomer?.id) {
        setSelectedCustomerId(preselectedCustomer.id);
      } else if (!selectedCustomerId && customers?.length > 0) {
        const custWithBalance = customers.find((c) => dueOf(c) > 0);
        setSelectedCustomerId(custWithBalance ? custWithBalance.id : customers[0].id);
      }
      setAmountReceived("");
      setReference("");
      setNotes("");
      setErrorMessage("");
      setIsManualModified(false);
      setPaymentDate(new Date().toISOString().split("T")[0]);
    }
  }, [isOpen, preselectedCustomer, customers, isEditing]);

  // Current selected customer entity
  const currentCustomer = useMemo(() => {
    if (!selectedCustomerId) return null;
    return customers.find((c) => c.id === selectedCustomerId) ||
      preselectedCustomer ||
      (isEditing ? { id: selectedCustomerId, name: editingPayment.partyName || editingPayment.customerName } : null);
  }, [selectedCustomerId, customers, preselectedCustomer, isEditing, editingPayment]);

  // Open invoices for the selected customer (balance > 0, oldest first).
  // When editing, this payment's own allocations are added back so they can be re-split.
  const openCustomerInvoices = useMemo(() => {
    if (!selectedCustomerId || !invoices) return [];
    return invoices
      .map((inv) => {
        const oldAlloc = editAllocByInvoice[inv.id] || 0;
        if (!oldAlloc) return inv;
        const paid = Number(inv.paid !== undefined ? inv.paid : (inv.paidAmount || 0));
        const balance = Number(inv.balance !== undefined ? inv.balance : Math.max(0, Number(inv.grandTotal || 0) - paid));
        return { ...inv, paid: Math.max(0, paid - oldAlloc), balance: balance + oldAlloc };
      })
      .filter((inv) => {
        const matchesCustomer =
          inv.customerId === selectedCustomerId ||
          (currentCustomer?.name && inv.customerName && inv.customerName.toLowerCase() === currentCustomer.name.toLowerCase());
        const bal = Number(inv.balance !== undefined ? inv.balance : Math.max(0, Number(inv.grandTotal || 0) - Number(inv.paid || 0)));
        return matchesCustomer && bal > 0;
      })
      .sort((a, b) => {
        const aDate = new Date(a.invoiceDate || a.createdAt || 0).getTime();
        const bDate = new Date(b.invoiceDate || b.createdAt || 0).getTime();
        if (aDate !== bDate) return aDate - bDate;
        return String(a.invoiceNo || "").localeCompare(String(b.invoiceNo || ""), undefined, { numeric: true });
      });
  }, [selectedCustomerId, invoices, currentCustomer, editAllocByInvoice]);

  // Total balance due across open invoices
  const totalOpenBalance = useMemo(() => {
    return openCustomerInvoices.reduce((sum, inv) => {
      const bal = Number(inv.balance !== undefined ? inv.balance : Math.max(0, Number(inv.grandTotal || 0) - Number(inv.paid || 0)));
      return sum + bal;
    }, 0);
  }, [openCustomerInvoices]);

  // Sync FIFO allocations whenever amountReceived or openCustomerInvoices changes (unless manually edited)
  useEffect(() => {
    if (!isManualModified) {
      const result = calculateFifoAllocations(openCustomerInvoices, amountReceived);
      setAllocations(result.allocations);
    }
  }, [openCustomerInvoices, amountReceived, isManualModified]);

  // Editing: start from the payment's original split across invoices
  const seededAllocIdRef = useRef(null);
  useEffect(() => {
    if (!isOpen) {
      seededAllocIdRef.current = null;
      return;
    }
    if (!isEditing || seededAllocIdRef.current === editingPayment.id || openCustomerInvoices.length === 0) return;
    if (selectedCustomerId !== (editingPayment.partyId || editingPayment.customerId)) return; // wait for party switch
    seededAllocIdRef.current = editingPayment.id;
    const base = calculateFifoAllocations(openCustomerInvoices, 0).allocations;
    setAllocations(base.map((a) => {
      const amt = Math.min(editAllocByInvoice[a.invoiceId] || 0, a.balanceDue);
      const rem = Math.round(Math.max(0, a.balanceDue - amt) * 100) / 100;
      return {
        ...a,
        allocatedAmount: amt,
        remainingBalance: rem,
        nextStatus: rem === 0 ? "Paid" : (a.alreadyPaid + amt > 0 ? "Partial" : "Unpaid")
      };
    }));
  }, [isOpen, isEditing, editingPayment, openCustomerInvoices, editAllocByInvoice, selectedCustomerId]);

  // Calculation summaries
  const totalApplied = useMemo(() => {
    return allocations.reduce((sum, a) => sum + Number(a.allocatedAmount || 0), 0);
  }, [allocations]);

  const receivedNum = Math.max(0, Number(amountReceived) || 0);
  const unallocatedAmount = Math.max(0, Math.round((receivedNum - totalApplied) * 100) / 100);

  // Reset to auto-FIFO
  const handleResetToFifo = () => {
    setIsManualModified(false);
    const result = calculateFifoAllocations(openCustomerInvoices, amountReceived);
    setAllocations(result.allocations);
    setErrorMessage("");
  };

  // Quick fill full outstanding balance
  const handlePayFullOutstanding = () => {
    setIsManualModified(false);
    setAmountReceived(String(totalOpenBalance));
  };

  // Handle manual per-row allocation change
  const handleRowAllocationChange = (invoiceId, newAmountStr) => {
    setIsManualModified(true);
    const val = newAmountStr === "" ? 0 : Math.max(0, Number(newAmountStr) || 0);

    const updated = allocations.map((a) => {
      if (a.invoiceId === invoiceId) {
        const cappedAmount = Math.min(val, a.balanceDue);
        const rem = Math.round(Math.max(0, a.balanceDue - cappedAmount) * 100) / 100;
        const status = rem === 0 ? "Paid" : (a.alreadyPaid + cappedAmount > 0 ? "Partial" : "Unpaid");
        return {
          ...a,
          allocatedAmount: cappedAmount,
          remainingBalance: rem,
          nextStatus: status
        };
      }
      return a;
    });

    setAllocations(updated);

    const validation = validateManualAllocations(updated, amountReceived);
    if (!validation.isValid) {
      setErrorMessage(validation.error);
    } else {
      setErrorMessage("");
    }
  };

  // Customer dropdown filter list
  const filteredCustomersForPicker = useMemo(() => {
    const term = customerSearch.toLowerCase().trim();
    return customers.filter((c) => {
      if (!term) return true;
      const name = (c.name || "").toLowerCase();
      const phone = (c.mobile || "").toLowerCase();
      return name.includes(term) || phone.includes(term);
    });
  }, [customers, customerSearch]);

  // Handle Save
  const handleSavePayment = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!selectedCustomerId) {
      setErrorMessage("Please select a customer.");
      return;
    }

    if (receivedNum <= 0) {
      setErrorMessage("Please enter a valid amount received.");
      return;
    }

    if (totalApplied <= 0 && unallocatedAmount <= 0) {
      setErrorMessage("Please allocate payment across at least one invoice.");
      return;
    }

    setSubmitting(true);
    try {
      if (isEditing) {
        const updated = await updatePayment(editingPayment.id, {
          amount: receivedNum,
          totalApplied,
          unallocatedAmount,
          paymentDate,
          paymentMode,
          reference,
          notes,
          allocations
        });
        if (refetchInvoices) await refetchInvoices();
        if (refetchCustomers) await refetchCustomers();
        if (onPaymentSaved) onPaymentSaved(updated);
        onClose();
        return;
      }

      const result = await recordCustomerPaymentTally({
        customerId: selectedCustomerId,
        customerName: currentCustomer?.name || "Customer",
        customerPhone: currentCustomer?.mobile || "",
        companyId: activeCompanyId || null,
        amount: receivedNum,
        totalApplied,
        unallocatedAmount,
        paymentDate,
        paymentMode,
        reference,
        notes,
        allocations
      });

      if (refetchInvoices) await refetchInvoices();
      if (refetchCustomers) await refetchCustomers();
      if (onPaymentSaved) onPaymentSaved();

      // Show receipt modal immediately after successful save
      if (result?.paymentRecord) {
        setSavedPaymentRecord(result.paymentRecord);
        setShowReceiptModal(true);
      }

      onClose();
    } catch (err) {
      console.error("Payment tally failed:", err);
      setErrorMessage(err.message || "Failed to save payment tally.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `✏️ Edit Receipt ${editingPayment.paymentNo || ""}` : "💰 Record Customer Payment"}
      size="lg"
    >
      <form onSubmit={handleSavePayment} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        
        {/* Helper Subtitle */}
        <div style={{ fontSize: "0.82rem", color: "var(--text-dim)", marginTop: "-0.5rem" }}>
          {isEditing
            ? "Change the amount, date, mode or how it is split. Invoice balances and the customer's due are corrected automatically."
            : "Settle customer dues across multiple invoices from oldest to newest (FIFO)."}
        </div>

        {/* 1. Customer Card */}
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            padding: "0.85rem 1rem",
            borderRadius: "var(--radius-md)"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
            <span style={{ fontSize: "0.72rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Customer Details
            </span>
            {totalOpenBalance > 0 && (
              <Badge variant="warning" style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }}>
                Total Due: ₹{totalOpenBalance.toLocaleString("en-IN")}
              </Badge>
            )}
          </div>

          {preselectedCustomer || isEditing ? (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
              <div>
                <div style={{ fontWeight: "700", fontSize: "1.05rem", color: "var(--text-main)" }}>
                  {currentCustomer?.name}
                </div>
                {currentCustomer?.mobile && (
                  <div style={{ fontSize: "0.8rem", color: "var(--text-dim)", marginTop: "2px" }}>
                    📞 {currentCustomer.mobile}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <span style={{ fontSize: "0.72rem", color: "var(--text-dim)" }}>Unpaid Invoices</span>
                <div style={{ fontWeight: "700", fontSize: "1.05rem", color: "var(--accent-blue)" }}>
                  {openCustomerInvoices.length} bill{openCustomerInvoices.length === 1 ? "" : "s"}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="form-control"
                onClick={() => setIsCustomerDropdownOpen(!isCustomerDropdownOpen)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  textAlign: "left",
                  cursor: "pointer",
                  fontWeight: currentCustomer ? "600" : "normal"
                }}
              >
                <span>
                  {currentCustomer
                    ? `${currentCustomer.name} (Due: ₹${Number(dueOf(currentCustomer) || 0).toLocaleString("en-IN")})`
                    : "Select a customer..."}
                </span>
                <span style={{ color: "var(--text-dim)", marginLeft: "0.5rem" }}>▾</span>
              </button>

              {isCustomerDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-subtle)",
                    borderRadius: "var(--radius-md)",
                    boxShadow: "var(--shadow-sheet)",
                    zIndex: 100,
                    maxHeight: "220px",
                    overflowY: "auto"
                  }}
                >
                  <div style={{ padding: "0.5rem", borderBottom: "1px solid var(--border-subtle)" }}>
                    <input
                      type="text"
                      placeholder="Search customer by name or phone..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="form-control"
                      style={{ height: "36px", minHeight: "36px", fontSize: "0.85rem" }}
                      autoFocus
                    />
                  </div>
                  {filteredCustomersForPicker.length === 0 ? (
                    <div style={{ padding: "0.75rem", fontSize: "0.85rem", color: "var(--text-dim)", textAlign: "center" }}>
                      No customers found.
                    </div>
                  ) : (
                    filteredCustomersForPicker.map((c) => {
                      const bal = Number(dueOf(c) || 0);
                      return (
                        <div
                          key={c.id}
                          onClick={() => {
                            setSelectedCustomerId(c.id);
                            setIsCustomerDropdownOpen(false);
                            setCustomerSearch("");
                            setIsManualModified(false);
                          }}
                          style={{
                            padding: "0.6rem 0.85rem",
                            borderBottom: "1px solid var(--border-subtle)",
                            cursor: "pointer",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            background: c.id === selectedCustomerId ? "var(--primary-glow)" : "transparent"
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: "600", fontSize: "0.88rem", color: "var(--text-main)" }}>
                              {c.name}
                            </div>
                            {c.mobile && (
                              <div style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>
                                📞 {c.mobile}
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <span style={{ fontSize: "0.82rem", fontWeight: "700", color: bal > 0 ? "var(--warning)" : "var(--text-muted)" }}>
                              ₹{bal.toLocaleString("en-IN")}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 2. Hero Payment Inputs */}
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            padding: "1rem",
            borderRadius: "var(--radius-md)",
            display: "flex",
            flexDirection: "column",
            gap: "0.85rem"
          }}
        >
          {/* Amount Received Hero Field */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
              <label className="form-label" style={{ margin: 0, fontWeight: "700", color: "var(--text-main)" }}>
                Amount Received (₹) <span style={{ color: "var(--danger)" }}>*</span>
              </label>
              {totalOpenBalance > 0 && (
                <button
                  type="button"
                  onClick={handlePayFullOutstanding}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--accent-blue)",
                    fontSize: "0.78rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    textDecoration: "underline",
                    padding: 0
                  }}
                >
                  Pay Full (₹{totalOpenBalance.toLocaleString("en-IN")})
                </button>
              )}
            </div>
            
            <div className="currency-input-wrap">
              <span className="currency-prefix" style={{ fontSize: "1.25rem", color: "var(--text-muted)" }}>₹</span>
              <input
                type="number"
                min="0"
                step="any"
                className="form-control"
                placeholder="0.00"
                value={amountReceived}
                onChange={(e) => {
                  setAmountReceived(e.target.value);
                  setIsManualModified(false);
                }}
                required
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "800",
                  color: "var(--accent-blue)",
                  paddingLeft: "2.25rem",
                  height: "48px",
                  minHeight: "48px"
                }}
                autoFocus={!isEditing}
              />
            </div>
          </div>

          {/* Payment Date, Mode, Ref in Clean Responsive Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.75rem" }}>
            <div>
              <label className="form-label">Payment Date <span style={{ color: "var(--danger)" }}>*</span></label>
              <input
                type="date"
                className="form-control"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="form-label">Payment Mode <span style={{ color: "var(--danger)" }}>*</span></label>
              <select
                className="form-control"
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
              >
                {PAYMENT_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Reference # / UTR</label>
              <input
                type="text"
                className="form-control"
                placeholder="Cheque / UTR / Txn ID"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="form-label">Notes / Remarks</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. Settlement for September invoices"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        {/* 3. Live Allocation Summary Box */}
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            padding: "0.75rem 1rem",
            borderRadius: "var(--radius-md)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: "0.68rem", color: "var(--text-dim)", textTransform: "uppercase", fontWeight: "600" }}>Received</div>
              <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "var(--text-main)" }}>
                ₹{receivedNum.toLocaleString("en-IN")}
              </div>
            </div>
            
            <div style={{ height: "24px", width: "1px", background: "var(--border-subtle)" }} />
            
            <div>
              <div style={{ fontSize: "0.68rem", color: "var(--text-dim)", textTransform: "uppercase", fontWeight: "600" }}>Applied to Bills</div>
              <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "var(--accent-blue)" }}>
                ₹{totalApplied.toLocaleString("en-IN")}
              </div>
            </div>

            {unallocatedAmount > 0 && (
              <>
                <div style={{ height: "24px", width: "1px", background: "var(--border-subtle)" }} />
                <div>
                  <div style={{ fontSize: "0.68rem", color: "var(--warning)", textTransform: "uppercase", fontWeight: "700" }}>Advance / Excess</div>
                  <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "var(--warning)" }}>
                    ₹{unallocatedAmount.toLocaleString("en-IN")}
                  </div>
                </div>
              </>
            )}
          </div>

          {isManualModified && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={handleResetToFifo}
              style={{ fontSize: "0.75rem", padding: "0.25rem 0.65rem", minHeight: "28px" }}
            >
              🔄 Auto-Allocate (FIFO)
            </Button>
          )}
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div style={{ background: "rgba(239, 68, 68, 0.12)", border: "1px solid rgba(239, 68, 68, 0.35)", padding: "0.65rem 0.85rem", borderRadius: "var(--radius-md)", color: "#fca5a5", fontSize: "0.85rem" }}>
            ⚠️ {errorMessage}
          </div>
        )}

        {/* 4. Open Invoices to Settle List */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
            <span style={{ fontSize: "0.75rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Invoices to Settle (Oldest First)
            </span>
            <span style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>
              {openCustomerInvoices.length} unpaid bill{openCustomerInvoices.length === 1 ? "" : "s"}
            </span>
          </div>

          {openCustomerInvoices.length === 0 ? (
            <Card style={{ textAlign: "center", padding: "1.5rem", color: "var(--text-dim)" }}>
              ✅ All invoices for this customer are fully paid! No outstanding balance.
            </Card>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem", maxHeight: "340px", overflowY: "auto", paddingRight: "4px" }}>
              {allocations.map((row) => {
                const isFullyCovered = row.remainingBalance === 0 && row.allocatedAmount > 0;
                const isPartial = row.remainingBalance > 0 && row.allocatedAmount > 0;

                return (
                  <div
                    key={row.invoiceId}
                    style={{
                      background: row.allocatedAmount > 0 ? "rgba(56, 189, 248, 0.04)" : "var(--bg-card)",
                      border: row.allocatedAmount > 0 ? "1px solid rgba(56, 189, 248, 0.35)" : "1px solid var(--border-subtle)",
                      borderRadius: "var(--radius-md)",
                      padding: "0.85rem 1rem",
                      transition: "border-color 0.2s, background 0.2s"
                    }}
                  >
                    {/* Invoice Info Header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                          <span style={{ fontWeight: "700", color: "var(--text-main)", fontSize: "0.98rem" }}>
                            {row.invoiceNo}
                          </span>
                          <Badge variant={row.isNonGst ? "neutral" : "info"} style={{ fontSize: "0.68rem" }}>
                            {row.isNonGst ? "Non-GST" : "GST"}
                          </Badge>
                          <span style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>
                            📅 {formatAppDate(row.invoiceDate, defaultCompany?.dateFormat)}
                          </span>
                        </div>
                        <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "2px" }}>
                          Grand Total: ₹{row.grandTotal.toLocaleString("en-IN")}
                          {row.alreadyPaid > 0 && ` • Paid: ₹${row.alreadyPaid.toLocaleString("en-IN")}`}
                        </div>
                      </div>

                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: "0.7rem", color: "var(--text-dim)", textTransform: "uppercase" }}>Balance Due</div>
                        <div style={{ fontWeight: "800", fontSize: "1.05rem", color: "var(--warning)" }}>
                          ₹{row.balanceDue.toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>

                    {/* Settle Row */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingTop: "0.65rem",
                        borderTop: "1px solid var(--border-subtle)",
                        marginTop: "0.65rem",
                        gap: "0.5rem",
                        flexWrap: "wrap"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "0.82rem", fontWeight: "600", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                          Settle:
                        </span>
                        
                        <div className="currency-input-wrap" style={{ width: "125px" }}>
                          <span className="currency-prefix" style={{ left: "0.65rem", fontSize: "0.85rem" }}>₹</span>
                          <input
                            type="number"
                            min="0"
                            max={row.balanceDue}
                            step="any"
                            value={row.allocatedAmount || ""}
                            placeholder="0"
                            onChange={(e) => handleRowAllocationChange(row.invoiceId, e.target.value)}
                            className="form-control"
                            style={{
                              height: "36px",
                              minHeight: "36px",
                              fontSize: "0.95rem",
                              fontWeight: "700",
                              color: row.allocatedAmount > 0 ? "var(--success)" : "var(--text-main)",
                              paddingLeft: "1.65rem",
                              paddingRight: "0.5rem"
                            }}
                          />
                        </div>

                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => handleRowAllocationChange(row.invoiceId, String(row.balanceDue))}
                          style={{ fontSize: "0.7rem", padding: "0.2rem 0.5rem", minHeight: "28px" }}
                        >
                          Full
                        </Button>
                      </div>

                      <div>
                        {isFullyCovered ? (
                          <Badge variant="success" style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }}>
                            ✓ Fully Settled
                          </Badge>
                        ) : isPartial ? (
                          <Badge variant="warning" style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }}>
                            Partial (Due: ₹{row.remainingBalance.toLocaleString("en-IN")})
                          </Badge>
                        ) : (
                          <Badge variant="danger" style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }}>
                            Unpaid
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 5. Sticky Action Footer */}
        <div
          style={{
            position: "sticky",
            bottom: "-1.25rem",
            margin: "0 -1.25rem -1.25rem -1.25rem",
            padding: "0.85rem 1.25rem",
            background: "var(--bg-surface)",
            borderTop: "1px solid var(--border-subtle)",
            display: "flex",
            justifyContent: "flex-end",
            gap: "0.75rem",
            zIndex: 10
          }}
        >
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
            style={{ minHeight: "42px", minWidth: "90px" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || openCustomerInvoices.length === 0 || totalApplied <= 0}
            style={{ minHeight: "42px", flex: 1, maxWidth: "340px", fontSize: "0.92rem", fontWeight: "700" }}
          >
            {submitting
              ? "Saving Payment..."
              : `${isEditing ? "Save Changes" : "Save Payment"} (₹${totalApplied.toLocaleString("en-IN")})`}
          </Button>
        </div>

      </form>
    </BottomSheet>

    {/* Auto-show receipt after payment is saved */}
    <PaymentReceiptModal
      isOpen={showReceiptModal}
      onClose={() => { setShowReceiptModal(false); setSavedPaymentRecord(null); }}
      payment={savedPaymentRecord}
    />
    </>
  );
}

export default PaymentTallyModal;

